import React, { useMemo } from 'react';
import { styled as styled2 } from '@compiled/react';
import { useFragment, graphql } from 'react-relay';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { BreadcrumbRenderer, ProjectBreadcrumb } from '@atlassian/jira-breadcrumbs/src';
import PageHeader from '@atlassian/jira-common-components-page-header';
import {
	FeedbackCollectorButton,
	EntryKeys,
	ENTRYPOINT_ID_JIRA_SOFTWARE_PROJECT,
} from '@atlassian/jira-feedback-collector';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src';
import Graph from '@atlassian/jira-graph';
import { useIntl } from '@atlassian/jira-intl';
import { createAri } from '@atlassian/jira-platform-ari';
import type { ProjectContext } from '@atlassian/jira-providers-project-context/src/types';
import type {
	main_softwareCodePage_Header$key,
	DevOpsProviderNamespace,
} from '@atlassian/jira-relay/src/__generated__/main_softwareCodePage_Header.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { FEEDBACK_COLLECTOR_LOCATION } from '../../common/constants';
import MoreMenu from '../../common/ui/more-menu';
import messages from './messages';

type Props = {
	headerData: main_softwareCodePage_Header$key;
	projectContext: ProjectContext;
	isAdmin: boolean;
};

const isOAuthProvider = (providerNamespace: DevOpsProviderNamespace | null | undefined): boolean =>
	providerNamespace === 'OAUTH';

export const Header = ({ projectContext, headerData, isAdmin }: Props) => {
	const breadcrumbs = <BreadcrumbRenderer />;
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();

	const data = useFragment(
		graphql`
			fragment main_softwareCodePage_Header on Query {
				devOps @required(action: THROW) {
					providers(id: $siteAri, providerTypes: [DEV_INFO])
						@required(action: THROW)
						@optIn(to: "DevOpsSecurityInJira") {
						...main_softwareCodePage_MoreMenu
						devInfoProviders {
							__typename
							name
							namespace
						}
					}
				}
			}
		`,
		headerData,
	);

	const providers = useMemo(() => data.devOps?.providers?.devInfoProviders, [data]);

	const additionalFields = useMemo(
		() => [
			{
				id: EntryKeys.CODE_IN_JIRA_PROVIDERS,
				value: providers
					? providers
							.map((provider) => {
								const isOauthProvider = isOAuthProvider(provider?.namespace);
								return isOauthProvider ? 'UGC provider name hidden' : provider?.name ?? '';
							})
							.join(', ')
					: '',
			},
			{ id: EntryKeys.CLOUD_ID, value: cloudId },
		],
		[providers, cloudId],
	);

	const baseUrl = '';

	const { projectKey, projectName, projectId } = projectContext;

	return (
		<Wrapper>
			<ProjectBreadcrumb baseUrl={baseUrl} projectKey={projectKey} projectName={projectName}>
				<PageHeader truncateTitle disableTitleStyles breadcrumbs={breadcrumbs}>
					<DocumentTitle title={formatMessage(messages.title)} />
					<TitleContainer>
						<Heading size="large" as="h1">
							<PageTitle data-testid="software-code-page.ui.header.software-code">
								{formatMessage(messages.title)}
							</PageTitle>
						</Heading>
						<ButtonsContainer>
							<Graph
								from={createAri({
									resourceId: projectId.toString(),
									resourceType: 'project',
									resourceOwner: 'jira',
									cloudId,
								})}
							/>
							<FeedbackCollectorButton
								entrypointId={ENTRYPOINT_ID_JIRA_SOFTWARE_PROJECT}
								feedbackGroupId={FEEDBACK_COLLECTOR_LOCATION}
								feedbackContext={additionalFields}
							/>
							<MoreMenu
								projectContext={projectContext}
								moreMenuData={data.devOps?.providers ?? null}
								isAdmin={isAdmin}
							/>
						</ButtonsContainer>
					</TitleContainer>
				</PageHeader>
			</ProjectBreadcrumb>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled2.div({
	padding: `0 ${token('space.500', '40px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleContainer = styled2.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	height: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonsContainer = styled2.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageTitle = styled2.div({
	marginTop: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const visibleForTesting = {
	PageTitle,
};
